import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import API from '../utils/tenantApi';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, formatDate } from '../utils/format';

// ── Helpers ──────────────────────────────────────────────────────────────────

function getStatusMessage(ledger) {
  const { inArrears, daysAhead = 0, arrearsDays = 0, arrearsAmount = 0 } = ledger;
  if (!inArrears) {
    if (daysAhead >= 7) return "Paid up and well ahead — great work!";
    if (daysAhead >= 1) return "You're ahead of schedule — nice one!";
    return "All paid up — nothing owing.";
  }
  if (arrearsDays > 14) return "Your account needs attention. Please contact your landlord.";
  if (arrearsDays > 7)  return `${formatCurrency(arrearsAmount)} is outstanding. Please pay when you can.`;
  return `A little behind — ${formatCurrency(arrearsAmount)} will bring you current.`;
}

function getStatusCardStyle(ledger) {
  if (!ledger.inArrears) {
    return { background: '#DCFCE7', border: '2px solid #16A34A', color: '#15803D' };
  }
  if (ledger.arrearsDays <= 7) {
    return { background: '#FEF9C3', border: '2px solid #CA8A04', color: '#92400E' };
  }
  return { background: '#FEE2E2', border: '2px solid #DC2626', color: '#991B1B' };
}

function getPeriodCovered(receivedDate, leaseStartDate) {
  const toUTC = (s) => { const [y,m,d] = s.split('-').map(Number); return new Date(Date.UTC(y,m-1,d)); };
  const received   = toUTC(receivedDate);
  const leaseStart = toUTC(leaseStartDate);
  const daysSince  = Math.max(0, Math.floor((received - leaseStart) / 86400000));
  const periodIdx  = Math.floor(daysSince / 14);
  const pStart     = new Date(leaseStart.getTime() + periodIdx * 14 * 86400000);
  const pEnd       = new Date(pStart.getTime() + 13 * 86400000);
  const fmt = (d) => {
    const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getUTCDate()).padStart(2,'0')} ${M[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  };
  return `${fmt(pStart)} – ${fmt(pEnd)}`;
}

// ── Period status badge ───────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = {
    PAID:       { label: 'Paid',    cls: 'text-green-700 bg-green-50',   dot: 'bg-green-500'  },
    PARTIAL:    { label: 'Partial', cls: 'text-amber-700 bg-amber-50',   dot: 'bg-amber-400'  },
    OVERDUE:    { label: 'Overdue', cls: 'text-red-700 bg-red-50',       dot: 'bg-red-500'    },
    NO_PAYMENT: { label: 'Overdue', cls: 'text-red-700 bg-red-50',       dot: 'bg-red-500'    },
    AHEAD:      { label: 'Ahead',   cls: 'text-blue-700 bg-blue-50',     dot: 'bg-blue-400'   },
  }[status] || { label: status, cls: 'text-slate-600 bg-slate-100', dot: 'bg-slate-400' };

  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ── PDF download ──────────────────────────────────────────────────────────────
function downloadPDF({ tenant, property, tenancy, ledger }) {
  const yyyymm = new Date().toISOString().substring(0, 7);
  const payments = [...ledger.payments].reverse();
  const tableRows = payments.map(p => {
    const period = getPeriodCovered(p.received_date, tenancy.lease_start_date);
    return `<tr>
      <td style="padding:8px 14px;border-bottom:1px solid #eee">${formatDate(p.received_date)}</td>
      <td style="padding:8px 14px;border-bottom:1px solid #eee;font-family:monospace">${formatCurrency(p.amount)}</td>
      <td style="padding:8px 14px;border-bottom:1px solid #eee">${period}</td>
      <td style="padding:8px 14px;border-bottom:1px solid #eee;color:#16a34a;font-weight:600">Received</td>
    </tr>`;
  }).join('');
  const today = new Date().toISOString().split('T')[0];
  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">
  <title>RentFriendly-PaymentRecord-${yyyymm}</title>
  <style>body{font-family:Arial,Helvetica,sans-serif;padding:48px;color:#1e293b;max-width:800px;margin:0 auto}
  h1{font-size:20px;font-weight:700;margin:0 0 6px}.meta{font-size:13px;color:#64748b;margin-bottom:32px;line-height:1.6}
  table{width:100%;border-collapse:collapse;font-size:13px}
  th{background:#f8fafc;text-align:left;padding:10px 14px;border-bottom:2px solid #e2e8f0;font-weight:600;color:#475569}
  tr:last-child td{border-bottom:none}
  .footer{margin-top:48px;font-size:11px;color:#94a3b8;text-align:center;border-top:1px solid #e2e8f0;padding-top:16px}
  @media print{body{padding:24px}}</style></head><body>
  <h1>Rent Payment Record — ${property.address}</h1>
  <div class="meta">Tenant: <strong>${tenant.name}</strong> &nbsp;·&nbsp; Lease started: <strong>${formatDate(tenancy.lease_start_date)}</strong><br>Generated: ${formatDate(today)}</div>
  <table><thead><tr><th>Date Received</th><th>Amount</th><th>Period Covered</th><th>Status</th></tr></thead>
  <tbody>${tableRows}</tbody></table>
  <div class="footer">Generated by RentFriendly &mdash; rentfriendly.com.au</div>
  <script>setTimeout(function(){window.print();},400);</script></body></html>`;
  const win = window.open('', '_blank');
  if (win) { win.document.write(html); win.document.close(); }
}

// ── Main component ────────────────────────────────────────────────────────────
export default function TenantPortalPage() {
  const { user, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [tab, setTab]           = useState('schedule');
  const [mobileTab, setMobileTab] = useState('summary');

  const consentBannerType = location.state?.consentBanner || null;

  useEffect(() => {
    API.get('/tenant/portal')
      .then(({ data }) => setData(data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load your portal'))
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = () => { logout(); navigate('/tenant/login'); };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-sm text-slate-500">Loading your portal...</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="text-center">
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 px-4 py-3 rounded-xl">{error}</p>
        <button onClick={handleLogout} className="mt-3 text-sm text-slate-500 hover:underline">Sign out</button>
      </div>
    </div>
  );

  const { tenant, property, tenancy, ledger } = data;
  const periods = ledger.periods || [];
  const statusCardStyle = getStatusCardStyle(ledger);
  const statusMessage   = getStatusMessage(ledger);
  const overduePeriods  = periods.filter(p => p.status === 'OVERDUE' || p.status === 'NO_PAYMENT').length;
  const partialPeriods  = periods.filter(p => p.status === 'PARTIAL').length;

  // Lease badge
  const leaseType    = tenancy.lease_type || 'fixed';
  const leaseEndDate = tenancy.lease_end_date;
  const todayISO     = new Date().toISOString().split('T')[0];
  const isExpired    = leaseEndDate && leaseEndDate < todayISO;
  const leaseBadge = leaseType === 'periodic'
    ? { label: 'Month to month', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-400' }
    : leaseEndDate
      ? isExpired
        ? { label: `Fixed term · expired ${formatDate(leaseEndDate)}`, bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-400' }
        : { label: `Fixed term · until ${formatDate(leaseEndDate)}`, bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', dot: 'bg-slate-400' }
      : { label: 'Fixed term', bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-200', dot: 'bg-slate-300' };

  // ── Schedule table (full width) ───────────────────────────────────────────
  const ScheduleSection = () => (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-semibold text-slate-800 text-sm">Rent schedule</h2>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span>{periods.length} periods</span>
          {partialPeriods > 0 && <span className="text-amber-600 font-medium">{partialPeriods} partial</span>}
          {overduePeriods > 0 && <span className="text-red-600 font-medium">{overduePeriods} overdue</span>}
        </div>
      </div>

      {ledger.inArrears && (
        <div className="mx-4 mt-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-xs text-amber-800">
            <span className="font-semibold">Rent overdue by {ledger.arrearsDays} days</span>
            {' · '}{formatCurrency(ledger.arrearsAmount)} outstanding · Paid to {formatDate(ledger.paidToDate)}
          </p>
        </div>
      )}

      {/* Table header — same col layout as landlord */}
      <div className="hidden sm:grid grid-cols-12 gap-2 px-5 py-2.5 mt-3 bg-slate-50 border-b border-slate-100 text-xs font-medium text-slate-500 uppercase tracking-wide">
        <div className="col-span-1">#</div>
        <div className="col-span-2">Due date</div>
        <div className="col-span-3">Period</div>
        <div className="col-span-2">Amount due</div>
        <div className="col-span-2">Received</div>
        <div className="col-span-2 text-right">Status</div>
      </div>

      <div className="divide-y divide-slate-100">
        {[...periods].reverse().map(p => {
          const isOverdue = p.status === 'OVERDUE' || p.status === 'NO_PAYMENT';
          return (
            <div key={p.periodNumber}
              className="px-5 py-3.5"
              style={isOverdue ? { backgroundColor: '#FFFAFA' } : undefined}>

              {/* Desktop row */}
              <div className="hidden sm:grid grid-cols-12 gap-2 items-center">
                <div className="col-span-1 text-xs text-slate-400 font-medium">{p.periodNumber}</div>
                <div className={`col-span-2 text-sm font-semibold ${isOverdue ? 'text-red-600' : 'text-slate-700'}`}>
                  {formatDate(p.dueDate)}
                </div>
                <div className="col-span-3 text-xs text-slate-500">
                  {formatDate(p.dueDate)} &ndash; {formatDate(p.periodEnd)}
                </div>
                <div className="col-span-2 text-sm text-slate-700">
                  {formatCurrency(p.amountDue)}
                </div>
                <div className={`col-span-2 text-sm font-semibold ${
                  p.periodPaid === 0 ? 'text-red-400 italic text-xs font-normal' :
                  p.periodPaid >= p.amountDue ? 'text-green-700' : 'text-amber-600'
                }`}>
                  {p.periodPaid === 0 ? (isOverdue ? 'nothing received' : '—') : formatCurrency(p.periodPaid)}
                </div>
                <div className="col-span-2 flex justify-end">
                  <StatusBadge status={p.status} />
                </div>
              </div>

              {/* Mobile row */}
              <div className="sm:hidden flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${isOverdue ? 'text-red-600' : 'text-slate-800'}`}>
                      {formatDate(p.dueDate)}
                    </span>
                    <span className="text-xs text-slate-400">#{p.periodNumber}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {formatDate(p.dueDate)} &ndash; {formatDate(p.periodEnd)}
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-slate-500">Due {formatCurrency(p.amountDue)}</span>
                    {p.periodPaid > 0 && (
                      <span className={`text-xs font-medium ${p.periodPaid >= p.amountDue ? 'text-green-600' : 'text-amber-600'}`}>
                        &middot; Rcvd {formatCurrency(p.periodPaid)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 pt-0.5">
                  <StatusBadge status={p.status} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Payments list ─────────────────────────────────────────────────────────
  const PaymentsSection = () => (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800 text-sm">Payment history</h2>
        <button
          onClick={() => downloadPDF({ tenant, property, tenancy, ledger })}
          className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Download PDF
        </button>
      </div>

      {ledger.payments.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <p className="text-sm text-slate-500">No payments recorded yet.</p>
        </div>
      ) : (
        <>
          <div className="hidden sm:grid grid-cols-12 gap-2 px-5 py-2.5 bg-slate-50 border-b border-slate-100 text-xs font-medium text-slate-500 uppercase tracking-wide">
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Amount</div>
            <div className="col-span-5">Period covered</div>
            <div className="col-span-3 text-right">Status</div>
          </div>
          <div className="divide-y divide-slate-100">
            {[...ledger.payments].reverse().map(p => (
              <div key={p.id} className="px-5 py-3">
                <div className="hidden sm:grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-2 text-sm text-slate-700">{formatDate(p.received_date)}</div>
                  <div className="col-span-2 text-sm font-semibold text-green-700">{formatCurrency(p.amount)}</div>
                  <div className="col-span-5 text-xs text-slate-500">{getPeriodCovered(p.received_date, tenancy.lease_start_date)}</div>
                  <div className="col-span-3 flex justify-end">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full text-green-700 bg-green-50">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />Received
                    </span>
                  </div>
                </div>
                <div className="sm:hidden flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{formatCurrency(p.amount)}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{formatDate(p.received_date)}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full text-green-700 bg-green-50">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />Received
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-20 sm:pb-0">

      {/* Nav — matches landlord nav width */}
      <div className="h-0.5 bg-brand-600 w-full" />
      <nav className="bg-white border-b border-slate-200 sticky top-0.5 z-40 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-brand-600 rounded-lg flex items-center justify-center shadow-sm">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <span className="font-bold text-slate-800 text-[15px] tracking-tight">Rent<span className="text-brand-600">Friendly</span></span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs bg-brand-50 text-brand-700 border border-brand-200 px-2.5 py-1 rounded-full font-medium hidden sm:inline">
              Tenant portal
            </span>
            <Link to="/tenant/settings" className="text-sm text-slate-500 hover:text-slate-700 transition-colors px-2 py-1 rounded-lg hover:bg-slate-100 flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="hidden sm:inline">Preferences</span>
            </Link>
            <button onClick={handleLogout} className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1 rounded-lg hover:bg-slate-100">
              Sign out
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">

        {/* Consent banners */}
        {consentBannerType === 'given' && (
          <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
            <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-green-800">Electronic notices enabled</p>
              <p className="text-sm text-green-700 mt-0.5">Your landlord can now send formal notices to your email. Change this in <Link to="/tenant/settings" className="underline">Preferences</Link>.</p>
            </div>
          </div>
        )}
        {consentBannerType === 'refused' && (
          <div className="mb-5 p-4 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3">
            <svg className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-slate-700">Electronic notices declined</p>
              <p className="text-sm text-slate-500 mt-0.5">Your landlord will deliver notices by hand or post. Update in <Link to="/tenant/settings" className="underline">Preferences</Link>.</p>
            </div>
          </div>
        )}

        {/* Greeting */}
        <div className="mb-5">
          <h1 className="text-xl font-bold text-slate-800">Hi, {tenant.name.split(' ')[0]} <span>&#128075;</span></h1>
          <p className="text-sm text-slate-500 mt-0.5">{property.address}</p>
        </div>

        {/* Summary cards — full width row like landlord */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <div className="col-span-2 sm:col-span-1 rounded-2xl p-5" style={statusCardStyle}>
            <p className="text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: statusCardStyle.color, opacity: 0.75 }}>
              Paid to date
            </p>
            <p style={{ fontSize: '22px', fontWeight: 700, lineHeight: 1.15, color: statusCardStyle.color }}>
              {formatDate(ledger.paidToDate)}
            </p>
            {ledger.inArrears && (
              <p style={{ fontSize: '12px', fontWeight: 600, marginTop: '4px', color: statusCardStyle.color, opacity: 0.85 }}>
                {ledger.arrearsDays} days overdue
              </p>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1.5">Next due</p>
            <p className="text-base font-bold text-slate-800">{formatDate(ledger.upcomingDueDate)}</p>
            <p className="text-xs text-slate-400 mt-0.5">{formatCurrency(ledger.nextDueAmount)} due</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1.5">Fortnightly rent</p>
            <p className="text-base font-bold text-slate-800">{formatCurrency(tenancy.fortnightly_rent)}</p>
            <p className="text-xs text-slate-400 mt-0.5">every 14 days</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1.5">Balance</p>
            <p className={`text-base font-bold ${ledger.inArrears ? 'text-red-600' : 'text-green-700'}`}>
              {ledger.inArrears ? `${formatCurrency(ledger.arrearsAmount)} owing` : 'Up to date'}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              {ledger.inArrears ? `${ledger.arrearsDays} days overdue` : statusMessage}
            </p>
          </div>
        </div>

        {/* Lease badge */}
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-medium mb-6 ${leaseBadge.bg} ${leaseBadge.text} ${leaseBadge.border}`}>
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${leaseBadge.dot}`} />
          {leaseBadge.label}
        </div>

        {/* Tab bar — desktop */}
        <div className="hidden sm:flex gap-1 mb-4 border-b border-slate-200">
          {[
            { key: 'schedule', label: 'Rent schedule' },
            { key: 'payments', label: 'Payments' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors -mb-px ${
                tab === t.key
                  ? 'text-brand-600 border-b-2 border-brand-600'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label}
              {t.key === 'schedule' && overduePeriods > 0 && (
                <span className="ml-1.5 text-xs bg-red-500 text-white rounded-full px-1.5 py-0.5">
                  {overduePeriods}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Desktop content */}
        <div className="hidden sm:block">
          {tab === 'schedule' ? <ScheduleSection /> : <PaymentsSection />}
        </div>

        {/* Mobile content */}
        <div className="sm:hidden">
          {mobileTab === 'summary' && (
            <div>
              {/* Mobile summary is already in the cards above — just show lease badge note */}
              <p className="text-xs text-slate-400 text-center mt-4">Switch to Schedule or Payments below</p>
            </div>
          )}
          {mobileTab === 'schedule' && <ScheduleSection />}
          {mobileTab === 'payments' && <PaymentsSection />}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6 pb-4">
          Questions about your rent? Contact your landlord directly.
        </p>
      </div>

      {/* Mobile sticky bottom nav */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-30">
        <div className="flex">
          {[
            { key: 'summary',  label: 'Summary',  icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
            { key: 'schedule', label: 'Schedule', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
            { key: 'payments', label: 'Payments', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setMobileTab(t.key)}
              className={`flex-1 py-3 flex flex-col items-center gap-1 text-xs font-medium transition-colors relative ${
                mobileTab === t.key ? 'text-brand-600' : 'text-slate-500'
              }`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={t.icon} />
              </svg>
              {t.label}
              {t.key === 'schedule' && overduePeriods > 0 && (
                <span className="absolute top-2 right-1/4 w-2 h-2 bg-red-500 rounded-full" />
              )}
              {mobileTab === t.key && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-600" />}
            </button>
          ))}
        </div>
      </div>

    </div>
  );
}
